import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { guestService } from "@/services/guestService";
import { Play, Pause, SkipBack, SkipForward, Loader2, ScrollText } from "lucide-react";

export function FeaturedPlayer({ courseId = "getting-started", eyebrow = "Getting Started · watch first", maxWidthClass = "max-w-2xl" }) {
  const audioRef = useRef(null);
  const [course, setCourse] = useState(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [progress, setProgress] = useState(0);
  const [cache] = useState(() => ({}));

  useEffect(() => { guestService.publicCourse(courseId).then(setCourse).catch(() => {}); }, [courseId]);

  const loadAudio = useCallback(async (i) => {
    if (cache[i]) return cache[i];
    setLoadingAudio(true);
    try {
      const audio = await guestService.publicAudio(courseId, i);
      const src = `data:audio/mp3;base64,${audio}`;
      cache[i] = src;
      return src;
    } catch { return null; } finally { setLoadingAudio(false); }
  }, [cache, courseId]);

  const playSection = useCallback(async (i, autoplay = true) => {
    setIdx(i); setProgress(0);
    const src = await loadAudio(i);
    if (!src || !audioRef.current) return;
    audioRef.current.src = src;
    if (autoplay) { try { await audioRef.current.play(); setPlaying(true); } catch { setPlaying(false); } }
  }, [loadAudio]);

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (!audioRef.current.src) { await playSection(idx, true); return; }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { try { await audioRef.current.play(); setPlaying(true); } catch {} }
  };

  const onEnded = () => {
    if (course && idx < course.sections.length - 1) playSection(idx + 1, true);
    else setPlaying(false);
  };
  const onTime = () => { const a = audioRef.current; if (a && a.duration) setProgress((a.currentTime / a.duration) * 100); };

  if (!course) {
    return (
      <div className={`aspect-video ${maxWidthClass} bg-gradient-to-br from-[#1a1206] to-[#2a1f0a] border border-amber-500/30 flex items-center justify-center`} data-testid="featured-player-loading">
        <Loader2 className="h-6 w-6 text-amber-300 animate-spin" />
      </div>
    );
  }

  const section = course.sections[idx];

  return (
    <div className={maxWidthClass} data-testid="featured-player">
      <audio ref={audioRef} onEnded={onEnded} onTimeUpdate={onTime} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
      <div className="flex items-center gap-2 mb-2">
        <ScrollText className="h-4 w-4 text-amber-500" />
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-600">{eyebrow}</p>
      </div>

      {/* Stage */}
      <div className="aspect-video bg-gradient-to-br from-[#160f04] to-[#2c2110] border border-amber-500/30 relative overflow-hidden flex flex-col justify-center px-6 md:px-10" data-testid="featured-stage">
        <motion.div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          animate={{
            background: [
              "radial-gradient(50% 55% at 12% 25%, rgba(245,190,90,0.55), transparent 55%)",
              "radial-gradient(50% 55% at 85% 80%, rgba(245,190,90,0.55), transparent 55%)",
              "radial-gradient(50% 55% at 12% 25%, rgba(245,190,90,0.55), transparent 55%)",
            ],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute top-4 left-6 z-10 flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-amber-400/90 font-semibold">
            Chapter {idx + 1} / {course.sections.length}
          </span>
          {playing && (
            <span className="flex items-end gap-0.5 h-3" aria-hidden>
              {[0, 1, 2].map((b) => (
                <motion.span key={b} className="w-0.5 bg-amber-400" style={{ height: "30%" }}
                  animate={{ height: ["30%", "100%", "30%"] }}
                  transition={{ duration: 0.7, repeat: Infinity, delay: b * 0.15, ease: "easeInOut" }} />
              ))}
            </span>
          )}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }} className="relative z-10">
            <h3 className="font-display text-2xl md:text-4xl font-extrabold tracking-tighter text-amber-50 leading-tight mb-3 flex flex-wrap">
              {(section.title || "").split(" ").map((w, i) => (
                <motion.span key={`${idx}-${i}`}
                  initial={{ opacity: 0, y: 18, filter: "blur(5px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ delay: 0.12 + i * 0.06, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="mr-[0.28em] inline-block">{w}</motion.span>
              ))}
            </h3>
            <ul className="space-y-1.5">
              {section.bullets.slice(0, 4).map((b, i) => {
                const bcount = Math.min(4, section.bullets.length);
                const active = bcount ? Math.min(bcount - 1, Math.floor((progress / 100) * bcount)) : -1;
                return (
                  <motion.li key={`${idx}-${i}`}
                    initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + i * 0.3, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className={`flex items-start gap-2 text-sm md:text-base transition-colors duration-300 ${i === active && playing ? "text-amber-50" : "text-amber-100/75"}`}>
                    <motion.span className="mt-2 h-1 w-1 bg-amber-400 shrink-0"
                      animate={i === active && playing ? { scale: [1, 1.9, 1] } : { scale: 1 }}
                      transition={{ duration: 1.2, repeat: i === active && playing ? Infinity : 0 }} />
                    {b}
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>
        </AnimatePresence>
        {!playing && !loadingAudio && (
          <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center group" data-testid="featured-play-overlay" aria-label="Play">
            <span className="h-16 w-16 rounded-full bg-amber-300/90 group-hover:bg-amber-200 flex items-center justify-center transition-colors shadow-lg">
              <Play className="h-7 w-7 text-[#1a1206] ml-1" />
            </span>
          </button>
        )}
        {loadingAudio && (
          <div className="absolute bottom-4 left-6 flex items-center gap-2 text-amber-200/70 text-xs"><Loader2 className="h-4 w-4 animate-spin" /> Loading narration...</div>
        )}
      </div>

      {/* Controls */}
      <div className="h-1 bg-amber-900/30 w-full mt-2"><div className="h-full bg-amber-400 transition-all" style={{ width: `${progress}%` }} /></div>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => idx > 0 && playSection(idx - 1, true)} disabled={idx === 0}
          className="h-9 w-9 flex items-center justify-center border border-border hover:bg-secondary disabled:opacity-30 transition-colors" data-testid="featured-prev"><SkipBack className="h-4 w-4" /></button>
        <button onClick={togglePlay} className="h-10 px-4 flex items-center gap-2 bg-foreground text-background font-semibold hover:opacity-90 transition-opacity text-sm" data-testid="featured-play-button">
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />} {playing ? "Pause" : "Play the guide"}
        </button>
        <button onClick={() => idx < course.sections.length - 1 && playSection(idx + 1, true)} disabled={idx === course.sections.length - 1}
          className="h-9 w-9 flex items-center justify-center border border-border hover:bg-secondary disabled:opacity-30 transition-colors" data-testid="featured-next"><SkipForward className="h-4 w-4" /></button>
        <span className="text-sm text-muted-foreground ml-1 truncate">{course.title}</span>
      </div>
    </div>
  );
}
